import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Message } from '../../../types/chat'
import { useChat } from '../../../context/useChat'
import { useAuth } from '../../../context/useAuth'
import { useWorkspace } from '../../../context/useWorkspace'
import { isPersonalWorkspace } from '../../../utils/workspaceKind'
import { isHelperOnlyAccount } from '../../../utils/helperMembership'
import { featureFlag } from '../../../utils/featureFlags'
import { useToast } from '../../../context/useToast'
import { useOrbit } from '../../Common/Orbit/useOrbit'
import { withLcmChip } from '../../Common/Orbit/lcmOrbitSlices'
import { useLcmChipDispatchers } from '../../Common/Orbit/useLcmChipDispatchers'
import type { OrbitActionBridge } from '../../Common/Orbit/orbitSliceHelpers'
import { buildChannelRowSlices, buildMentionChipSlices, buildMessageBubbleSlices } from '../chat.orbit'
import { ORBIT_SLICE_TO_ACTION } from '../../Common/Orbit/orbitSliceIdMap'
import { invokeAction } from '../../../services/actions/registry'
import { useNls } from '../../../nls/nlsProvider'
import { Button } from '../../Common/Button'
import { ControlNotice } from '../../Common'
import { useChatControls } from '../../../hooks/useChatControls'
import { MemberChatHeader } from './MemberChatHeader'
import { MemberChatThread } from './MemberChatThread'
import { MemberChatComposer } from './MemberChatComposer'
import { MemberChannelPicker } from './MemberChannelPicker'
import { MemberChatContextSheet } from './MemberChatContextSheet'
import { ChannelSwitcherSheet } from './sheets/ChannelSwitcherSheet'
import { MembersSheet } from './sheets/MembersSheet'
import { AiHistorySheet } from './sheets/AiHistorySheet'
import { CreateChannelModal } from '../CreateChannelModal'
import { MemberSurfaceFrame, type MemberNexusChip } from '../../Member/controls/MemberNexusPrimitives'
import type { TeamGroupChatProps } from '../TeamGroupChat'
import './memberChat.css'

const MemberNotesMode = lazy(async () => {
  const module = await import('./MemberNotesMode')
  return { default: module.MemberNotesMode }
})

type Props = {
  onSendToAi?: (text: string) => void
  onOpenAiMagic?: () => void
  onElementMentionClick?: (elementId: string) => void
  helperSyncState?: string
  helperQuickActions?: TeamGroupChatProps['helperQuickActions']
  renderHelperSummary?: TeamGroupChatProps['renderHelperSummary']
  onTranslateMessage?: TeamGroupChatProps['onTranslateMessage']
  nexusChips?: MemberNexusChip[]
  helperMode?: boolean
}

/**
 * MemberChatScreen switches personal workspaces to the owner-only notes rail.
 * Team Chat remains isolated in its own component so its hook graph is never
 * conditionally invoked when the active workspace changes.
 */
export const MemberChatScreen = (props: Props) => {
  const { currentUser } = useAuth()
  const { activeWorkspaceId, workspaces } = useWorkspace()
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId)

  if (
    featureFlag('personalInMemberApp') &&
    activeWorkspace &&
    isPersonalWorkspace(activeWorkspace) &&
    currentUser?.uid
  ) {
    return (
      <Suspense fallback={null}>
        <MemberNotesMode workspaceId={activeWorkspace.id} ownerUid={currentUser.uid} />
      </Suspense>
    )
  }

  return <TeamMemberChatScreen {...props} />
}

/**
 * TeamMemberChatScreen — dedicated mobile chat surface for the Helper (member)
 * capacitor shell. Replaces the desktop 3-column ChatPage that used to render
 * inside the CHAT tab and was cramped on phones.
 *
 * Structure:
 *   header  → hamburger opens channels sheet, center opens members sheet,
 *             sparkle opens AI history sheet, ⋮ overflow (reserved).
 *   thread  → auto-scrolling message list with day dividers and author-run
 *             grouping (avatar on last-of-group, name on first-of-group).
 *   composer → sticky textarea with attach / AI / send.
 *   sheets  → bottom-sheet primitives for channels, members, AI history, and
 *             the long-press message-actions context.
 *
 * Reply is a single parent pointer (`Message.replyToId`) — the same field is
 * rendered by the shared MessageBubble on desktop/web too, so replies stay
 * legible on every surface without a "threads" concept.
 */
const TeamMemberChatScreen = ({
  onOpenAiMagic,
  renderHelperSummary,
  onTranslateMessage,
  nexusChips,
  helperMode = false,
}: Props) => {
  const { t } = useNls()
  const { currentUser, userProfile } = useAuth()
  const isHelper = helperMode || isHelperOnlyAccount({ userProfile, currentUser })
  const { activeWorkspaceId, workspaces, canPerform } = useWorkspace()
  const { mode: chatMode, canCreateChannel, canCreateDm } = useChatControls()
  const { showToast } = useToast()
  const chat = useChat()
  const { deleteMessage, renameChannel, leaveChannel } = chat
  const lcmDispatchers = useLcmChipDispatchers()
  const onOrbitAction = useCallback<OrbitActionBridge>((sliceId, label, id, title, phase6) => {
    if (phase6) {
      showToast('Coming in Orbit Phase 6')
      return
    }
    const actionId = ORBIT_SLICE_TO_ACTION[sliceId]
    if (actionId) {
      void invokeAction(actionId, { id, title }, {
        workspaceId: activeWorkspaceId ?? '',
        actor: { uid: currentUser?.uid ?? '' },
        canPerform,
        showToast,
        invokedFrom: 'orbit',
        chatOps: {
          deleteMessage: async (msgId: string) => {
            await deleteMessage(msgId)
          },
          renameChannel: async (chanId: string, newName: string) => {
            await renameChannel(chanId, newName)
          },
          leaveChannel: async (chanId: string) => {
            await leaveChannel(chanId)
          },
          replyToMessage: async () => {
            showToast('Reply ready', 'info')
          },
          convertToTask: async () => {
            showToast('Convert to task ready', 'info')
          },
          openDm: async () => {
            if (!canCreateDm) return
            showToast('Direct message ready', 'info')
          },
          configureNotifications: async () => {
            showToast('Notifications configured', 'info')
          },
        },
      })
      return
    }
    const lcmDispatch = lcmDispatchers[sliceId]
    if (lcmDispatch) {
      lcmDispatch(id, title)
      return
    }
    console.info('[orbit] unwired', sliceId)
    showToast(`${label}${title ? ` · ${title}` : ''}`)
    // TODO(orbit-handler-missing): sliceIds that hit this fallback still need real primitives — grep console for "[orbit] unwired" to enumerate.
  }, [activeWorkspaceId, canCreateDm, canPerform, currentUser?.uid, deleteMessage, lcmDispatchers, leaveChannel, renameChannel, showToast])
  const orbitDescriptor = useMemo(() => ({
    page: 'chat',
    centerLabel: 'Chat',
    onCreate: () => {
      if (!canCreateChannel) return
      chat.requestCreateChannelDraft({ name: '', type: 'public' })
    },
    contextSlices: () => [],
    targets: withLcmChip({
      'message-bubble': (node: HTMLElement) => buildMessageBubbleSlices(node, onOrbitAction),
      'channel-row': (node: HTMLElement) => buildChannelRowSlices(node, onOrbitAction),
      'mention-chip': (node: HTMLElement) => buildMentionChipSlices(node, onOrbitAction, canCreateDm),
    }, onOrbitAction),
  }), [canCreateChannel, canCreateDm, chat, onOrbitAction])
  useOrbit(orbitDescriptor)
  const {
    activeChannelId,
    channelsList,
    messagesStream,
    members,
    viewerPresence,
    typingPresence,
    unreadByChannel,
    setActiveChannelId,
    sendMessage,
    markRead,
  } = chat

  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const [contextMessage, setContextMessage] = useState<{ message: Message; isSelf: boolean } | null>(null)
  const [channelsOpen, setChannelsOpen] = useState(false)
  const [membersOpen, setMembersOpen] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [composerPrefill, setComposerPrefill] = useState<string | null>(null)
  const [translations, setTranslations] = useState<Record<string, string>>({})
  const [createChannelOpen, setCreateChannelOpen] = useState(false)
  const canCreateChannels = !helperMode && (canPerform('chat.create_channel') || canPerform('chat.create_private_channel'))

  const activeChannel = useMemo(
    () => channelsList.find((channel) => channel.id === activeChannelId) ?? null,
    [channelsList, activeChannelId],
  )

  const workspaceName = useMemo(
    () => workspaces.find((workspace) => workspace.id === activeWorkspaceId)?.name ?? '',
    [workspaces, activeWorkspaceId],
  )

  const onlineCount = viewerPresence.length
  const memberCount = members.length

  const typingLabel = useMemo(() => {
    const otherTyping = typingPresence.filter((entry) => entry.uid !== currentUser?.uid)
    if (otherTyping.length === 0) return null
    if (otherTyping.length === 1) return t('NLSChatTypingSingle', { name: otherTyping[0].displayName || t('NLSChatLabelSomeone') })
    return t('NLSChatTypingMulti', { count: String(otherTyping.length) })
  }, [typingPresence, currentUser?.uid, t])

  useEffect(() => {
    if (!activeChannelId) return
    void markRead(activeChannelId)
  }, [activeChannelId, markRead, messagesStream.length])

  const handleSend = useCallback(
    async (text: string, options: { replyToId?: string }) => {
      try {
        await sendMessage(text, options)
      } catch (error) {
        const messageText = error instanceof Error ? error.message : t('NLSChatSendFailed')
        showToast(messageText, 'error')
      }
    },
    [sendMessage, showToast, t],
  )

  const handleReply = useCallback((message: Message) => {
    setReplyTo(message)
  }, [])

  const handleCopy = useCallback(async (message: Message) => {
    const text = message.textContent || ''
    try {
      await navigator.clipboard.writeText(text)
      showToast(t('NLSChatToastCopied'), 'ghost')
    } catch {
      showToast(t('NLSChatToastCopyFailed'), 'error')
    }
  }, [showToast, t])

  const handleTranslate = useCallback(async (message: Message) => {
    if (!onTranslateMessage) return
    try {
      const translated = await onTranslateMessage(message)
      if (translated) setTranslations((current) => ({ ...current, [message.id]: translated }))
    } catch {
      showToast(t('NLSChatToastTranslateFailed'), 'error')
    }
  }, [onTranslateMessage, showToast, t])

  const helperFocusedSummary = useMemo<ReactNode | null>(() => {
    if (!renderHelperSummary || !activeChannelId) return null
    return renderHelperSummary(messagesStream, activeChannelId)
  }, [renderHelperSummary, messagesStream, activeChannelId])

  const canEditContext = contextMessage?.isSelf === true && !contextMessage?.message.isDeleted
  const canDeleteContext = contextMessage?.isSelf === true && !contextMessage?.message.isDeleted

  const emptyTitle = activeChannelId
    ? t('NLSChatEmptyChannelTitle', { channel: activeChannel?.name ?? t('NLSChatLabelChannel') })
    : t('NLSChatSelectChannel')
  const emptySub = activeChannelId
    ? t('NLSChatEmptyChannelSub')
    : t('NLSChatSelectChannelSub')

  if (chatMode === 'OFF') {
    return (
      <div className="memberChat memberChatControlNoticeOnly" data-testid="member-chat-screen" style={{ padding: '24px' }}>
        <ControlNotice
          tone="warning"
          message={t('NLSControlNoticeChatOff')}
          testId="chat-control-notice-off"
        />
      </div>
    )
  }

  return (
    <div className="memberChat" data-testid="member-chat-screen">
      <MemberSurfaceFrame
        className="memberChatSurface"
        bottomSlot={activeChannelId ? (
          <MemberChatComposer
            workspaceId={activeWorkspaceId ?? undefined}
            channel={activeChannel as Record<string, unknown> | null}
            whatsappRoute={workspaces.find((w) => w.id === activeWorkspaceId)?.settings?.whatsapp?.route}
            channelName={activeChannel?.name ?? t('NLSChatLabelChannel')}
            replyTo={replyTo}
            onClearReplyTo={() => setReplyTo(null)}
            disabled={!activeChannelId}
            onSend={handleSend}
            onOpenAi={onOpenAiMagic ? onOpenAiMagic : () => setAiOpen(true)}
            prefill={composerPrefill}
            onPrefillConsumed={() => setComposerPrefill(null)}
          />
        ) : undefined}
      >
        <MemberChatHeader
          channel={activeChannel}
          onlineCount={onlineCount}
          memberCount={memberCount}
          extraChips={nexusChips}
          helperMode={isHelper}
          onOpenChannels={() => setChannelsOpen(true)}
          onOpenMembers={() => setMembersOpen(true)}
          onOpenAi={() => setAiOpen(true)}
          onOpenMore={() => showToast(t('NLSChatToastMoreComingSoon'), 'ghost')}
        />

        {activeChannelId ? (
          <MemberChatThread
            messages={messagesStream}
            currentUserId={currentUser?.uid ?? null}
            translations={translations}
            typingLabel={typingLabel}
            onOpenContext={(message, isSelf) => setContextMessage({ message, isSelf })}
            emptyTitle={emptyTitle}
            emptySub={emptySub}
            emptyCta={activeChannel ? (
              <Button
                size="lg"
                variant="primary"
                onClick={() => setComposerPrefill(`Hi #${activeChannel.slug}`)}
              >
                {t('NLSChatActionStartConversation')}
              </Button>
            ) : undefined}
          />
        ) : (
          <MemberChannelPicker
            channels={channelsList}
            unreadByChannel={unreadByChannel}
            onSelect={setActiveChannelId}
          />
        )}
      </MemberSurfaceFrame>

      <ChannelSwitcherSheet
        open={channelsOpen}
        onClose={() => setChannelsOpen(false)}
        channels={channelsList}
        activeChannelId={activeChannelId}
        unreadByChannel={unreadByChannel}
        onSelect={(id) => setActiveChannelId(id)}
        workspaceName={workspaceName}
        onCreateChannel={canCreateChannels ? () => setCreateChannelOpen(true) : undefined}
      />

      <CreateChannelModal
        isOpen={createChannelOpen}
        onClose={() => setCreateChannelOpen(false)}
        onCreated={(channelId) => {
          setCreateChannelOpen(false)
          setChannelsOpen(false)
          setActiveChannelId(channelId)
        }}
      />

      {!isHelper && (
        <MembersSheet
          open={membersOpen}
          onClose={() => setMembersOpen(false)}
          members={members}
          viewerPresence={viewerPresence}
          channelName={activeChannel?.name ?? ''}
        />
      )}

      <AiHistorySheet
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        channelName={activeChannel?.name ?? ''}
        onOpenAiMagic={onOpenAiMagic}
        summarySlot={helperFocusedSummary}
      />

      <MemberChatContextSheet
        message={contextMessage?.message ?? null}
        isSelf={contextMessage?.isSelf ?? false}
        canEdit={canEditContext ?? false}
        canDelete={canDeleteContext ?? false}
        translateAvailable={!!onTranslateMessage}
        onClose={() => setContextMessage(null)}
        onReply={handleReply}
        onCopy={handleCopy}
        onTranslate={onTranslateMessage ? handleTranslate : undefined}
      />
    </div>
  )
}
